import React, { Component } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';

import { PRODUCT_REVIEWS_PER_PAGE } from 'constants/appConstants';

import styles from 'styles/containers/productReviews.scss';
import {
  exitSearchReviews,
  fetchProductReviews,
  fetchReviewSearchResults,
  upvoteReview
} from 'actions/productDetail';
import { trackEvent } from 'helpers/analytics';
import { err, setError } from 'actions/errors';
import { redirectTo } from 'actions/redirect';
import { hideReviewGalleryModal, isValidReviewsRequest, loadProductReviewsPage, setProductReviewsDocMeta, showReviewGalleryModal } from 'actions/reviews';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import Pagination from 'components/common/Pagination';
import ReviewSearch from 'components/reviews/ReviewSearch';
import marketplace from 'cfg/marketplace.json';
import ReviewGallery from 'containers/ReviewGallery';
import Review from 'components/reviews/Review';
import Rating from 'components/Rating';
import { makePageOneUrl } from 'helpers/ReviewUtils';
import MartyLink from 'components/common/MartyLink';
import ReviewSort from 'components/reviews/ReviewSort';
import { currentPathAndSearch, smoothScroll } from 'helpers/ClientUtils';
import { toThousandsSeparator } from 'helpers/NumberFormats';

const { features: { showReviews } } = marketplace;

export class ProductReviews extends Component {
  static fetchDataOnServer(store, location, params) {
    const { productId, reviewsPage, reviewsStart, orderBy } = params;
    if (+reviewsPage === 0) {
      const redirectAction = redirectTo(makePageOneUrl(location.pathname));
      return Promise.resolve(store.dispatch(redirectAction));
    } else if (isValidReviewsRequest(params)) {
      return store.dispatch(loadProductReviewsPage(productId, reviewsPage, reviewsStart, orderBy));
    } else {
      return Promise.resolve(store.dispatch(setError(err.PRODUCT_DETAILS,null,404)));
    }

  }

  constructor(props) {
    super(props);
    this.onNextPage = this.onNextPage.bind(this);
    this.onPreviousPage = this.onPreviousPage.bind(this);
    this.onSearchReviews = this.onSearchReviews.bind(this);
    this.onExitSearch = this.onExitSearch.bind(this);
  }

  componentDidMount() {
    const { product: { detail, reviewData }, fetchProductReviews, params, setProductReviewsDocMeta } = this.props;
    const { productId, reviewsPage, reviewsStart, orderBy } = params;

    // only fetch data if loaded product is not requested productId
    const productChanged = (!detail || (detail.productId !== productId));
    const incomingPage = reviewsPage || 1;
    const incomingOffset = reviewsStart || 0;
    if (productChanged) {
      this.fetchAllData(params);
    } else {
      if (incomingPage !== reviewData.page || incomingOffset !== reviewData.offset) {
        fetchProductReviews(productId, reviewsPage, reviewsStart, true, orderBy);
      }
      setProductReviewsDocMeta(detail);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      reviewGallery: { isModalOpen },
      product: { isSearchingReviews, searchReviewData: { searchTerm } },
      hideReviewGalleryModal,
      params,
      fetchReviewSearchResults,
      fetchProductReviews
    } = this.props;
    const { params: { reviewsPage: prevReviewsPage, orderBy: prevOrderBy, productId: prevProductId } } = prevProps;
    const { productId, reviewsPage, reviewsStart, orderBy } = params;
    const productIdChanged = (productId !== prevProductId);

    if (productIdChanged) {
      this.fetchAllData(params);
    } else if (reviewsPage !== prevReviewsPage || orderBy !== prevOrderBy) {
      if (isSearchingReviews) {
        if (!reviewsPage) {
          this.onExitSearch();
        } else {
          fetchReviewSearchResults(productId, searchTerm, reviewsPage);
        }
      } else {
        fetchProductReviews(productId, reviewsPage, reviewsStart, true, orderBy);
      }
    }
    if (isModalOpen && reviewsPage !== prevReviewsPage) {
      hideReviewGalleryModal();
    }
  }

  componentWillUnmount() {
    this.onExitSearch(false);
  }

  onExitSearch(backToReviews = true) {
    const { product: { detail: { productId }, isSearchingReviews }, exitSearchReviews } = this.props;
    if (isSearchingReviews) {
      const firstPage = this.makeReviewUrl(productId, 1, 0);
      exitSearchReviews();
      if (backToReviews) {
        this.context.router.pushPreserveAppRoot(firstPage);
      }
    }
  }

  onNextPage() {
    const { productId, reviewsPage, reviewsStart, reviewsTotalPages, orderBy } = this.getPaginationData();
    if (reviewsPage <= reviewsTotalPages) {
      const newLocation = this.makeReviewUrl(productId, reviewsPage + 1, reviewsStart, orderBy);
      this.context.router.pushPreserveAppRoot(newLocation);
    }
  }

  onPreviousPage() {
    const { productId, reviewsPage, reviewsStart, orderBy } = this.getPaginationData();
    if (reviewsPage > 0) {
      const newLocation = this.makeReviewUrl(productId, reviewsPage - 1, reviewsStart, orderBy);
      this.context.router.pushPreserveAppRoot(newLocation);
    }
  }

  onSearchReviews(e, searchTerm, doc = document) {
    e.preventDefault();
    const { fetchReviewSearchResults, product: { detail: { productId }, searchReviewData: { searchTerm: lastPerformedSearchTerm } } } = this.props;
    if (searchTerm && searchTerm !== lastPerformedSearchTerm) {
      // searching doesn't change the URL, so scroll to top so user doesn't seem a fully white screen while loading.
      if (doc) {
        smoothScroll.scrollTo(doc.body);
      }

      fetchReviewSearchResults(productId, searchTerm)
        .then(() => {
          const newLocation = this.makeReviewUrl(productId, 1, 0);
          this.context.router.pushPreserveAppRoot(newLocation);
        });

    } else if (!searchTerm) {
      this.onExitSearch(true);
    }
  }

  getPaginationData() {
    const {
      product: {
        detail = {},
        isSearchingReviews,
        reviewData: {
          page,
          offset,
          orderBy
        },
        searchReviewData: {
          page: searchPage,
          offset: searchOffset,
          totalPages: searchTotalPages
        },
        reviewsTotalPages
      }
    } = this.props;

    const { productId } = detail || {}; // detail can still be deliberately null.

    // this 100% should be in the reducer.
    // get appropriate offsets when user is searching reviews
    let reviewsPage, reviewsStart, totalPages;
    if (isSearchingReviews) {
      reviewsPage = searchPage;
      reviewsStart = searchOffset;
      totalPages = searchTotalPages;
    } else {
      reviewsPage = page;
      reviewsStart = offset;
      totalPages = reviewsTotalPages;
    }

    return {
      productId,
      reviewsPage: parseInt(reviewsPage, 10) || 1,
      reviewsStart: parseInt(reviewsStart, 10) || 0,
      reviewsTotalPages: parseInt(totalPages, 10) || 1,
      orderBy
    };
  }

  atLeastOneRatingHasPercentage = reviewSummary => {
    const { maxSizeRatingPercentage, maxWidthRatingPercentage, maxArchRatingPercentage } = reviewSummary;
    return reviewSummary && (this.hasPercentage(maxSizeRatingPercentage)
        || this.hasPercentage(maxWidthRatingPercentage)
        || this.hasPercentage(maxArchRatingPercentage));
  }

  fetchAllData({ productId, reviewsPage, reviewsStart, orderBy }) {
    this.props.loadProductReviewsPage(productId, reviewsPage, reviewsStart, orderBy);
  }

  handleReviewMediaClick = (reviewId, mediaIndex) => {
    const { showReviewGalleryModal } = this.props;
    showReviewGalleryModal(reviewId, mediaIndex);
  };

  handleReviewUpvoteClick = reviewId => {
    const { product: { reviewData: { loadingReviews } }, upvoteReview } = this.props;
    if (!loadingReviews.includes(reviewId)) {
      upvoteReview(reviewId, currentPathAndSearch());
      trackEvent('TE_REVIEWS_RATEREVIEWHELPFUL');
    }
  };

  handleSortReviewsClick = orderBy => {
    const { params: { productId, reviewsStart, orderBy: orderByParam }, exitSearchReviews } = this.props;
    if (orderByParam === orderBy) {
      return;
    }
    const newLocation = this.makeReviewUrl(productId, 1, reviewsStart, orderBy);
    exitSearchReviews();
    this.context.router.pushPreserveAppRoot(newLocation);
    trackEvent('TE_REVIEWS_SORTREVIEWBYNEW');
  };

  hasPercentage(value) {
    return value && value.percentage;
  }

  makePageHeading = () => {
    const {
      props: {
        product: {
          detail: { brandName, colorId, productName },
          detail
        }
      },
      context: { testId }
    } = this;
    return (
      <h1 className={styles.pageHeading} data-test-id={testId('viewProduct')}>
        <span>Customer Reviews</span>
        {' '}
        <Link className={styles.brandProductName} to={buildSeoProductUrl(detail, colorId)}>{brandName} {productName}</Link>
      </h1>
    );
  }

  makeReviewContent({ reviewsTotalPages, reviewsStart, reviewsPage, orderBy }) {
    const { context: { testId } } = this;
    const {
      product: {
        detail: { productName, productId, colorId },
        reviewData, searchReviewData, isSearchingReviews
      },
      showReviews, params, location
    } = this.props;

    if (!showReviews) {
      return null;
    }

    const { reviews: searchResults, searchTerm } = searchReviewData;
    const { reviews: productReviews, submittedReviews, loadingReviews } = reviewData;

    const reviewsToDisplay = isSearchingReviews ? searchResults : productReviews;
    let totalPagesWithOffset = reviewsTotalPages - Math.floor(reviewsStart / PRODUCT_REVIEWS_PER_PAGE);
    if (totalPagesWithOffset < 1) {
      totalPagesWithOffset = 1;
    }

    if (productReviews.length === 0) {
      return (
        <div className={styles.noReviewsContainer}>
          <div className={styles.noReviews} data-test-id={testId('noReviewsTextRP')}>
            This product currently has 0 reviews.
          </div>
        </div>
      );
    }

    return (
      <div className={styles.reviewListContainer}>
        <h2>Additional Reviews</h2>
        <ReviewSearch
          initialValue={searchTerm}
          searchReviews={this.onSearchReviews} />
        {reviewsToDisplay.length ? (
          <div>
            <div className={styles.reviewSortPaginate}>
              <div className={styles.reviewSort}>
                <ReviewSort orderBy={orderBy} onSortReviewsClick={this.handleSortReviewsClick}/>
              </div>
            </div>
            <div className={styles.reviewList}>
              {reviewsToDisplay.map(review => (
                <div key={review.id} className={styles.reviewWrapper}>
                  <Review
                    review={review}
                    productName={productName}
                    loadingReviews={loadingReviews}
                    submittedReviews={submittedReviews}
                    onReviewUpvoteClick={this.handleReviewUpvoteClick}
                    onReviewMediaClick={this.handleReviewMediaClick} />
                </div>
              ))}
            </div>
            <ReviewGallery params={params} returnUrl={`/product/review/${productId}${colorId ? `/color/${colorId}` : ''}`}/>
          </div>
        ) : (
          <div className={styles.noReviews} data-test-id={testId('noSearchResults')}>
            We were unable to find anything related to your search. If you can’t find it here, check out our <MartyLink to="/c/general-questions">Customer Questions Section</MartyLink> or <MartyLink to="/c/contact-us">Contact Us</MartyLink> to get advice from one of our helpful call center representatives.
          </div>
        )}
        <div className={styles.paginationWrapper} data-test-id={testId('paginationControls')}>
          <Pagination
            currentLocation={location.pathname}
            firstPageIndex={1}
            page={reviewsPage}
            desktopStyled={true}
            totalPages={totalPagesWithOffset} />
        </div>
      </div>
    );
  }

  makeReviewDetailSummary() {
    const {
      props: {
        product: {
          detail: {
            reviewSummary: {
              reviewingAShoe, overallRating, maxSizeRatingPercentage,
              maxWidthRatingPercentage, maxArchRatingPercentage
            },
            reviewCount, reviewSummary
          },
          detail, styleThumbnails
        }
      },
      context: { testId },
      atLeastOneRatingHasPercentage, makeTotalText
    } = this;

    const reviewsTotalCount = cn(styles.reviewsTotalCount, { [styles.reviewNonShoe]: !reviewingAShoe || !atLeastOneRatingHasPercentage(reviewSummary) });
    const reviewActions = cn(styles.reviewActions, { [styles.reviewNonShoe]: !reviewingAShoe || !atLeastOneRatingHasPercentage(reviewSummary) });
    const reviewRatingSummary = cn(styles.reviewRatingSummary, { [styles.reviewNonShoe]: !reviewingAShoe || !atLeastOneRatingHasPercentage(reviewSummary) });

    const numbers = [5, 4, 3, 2, 1];
    if (reviewCount) {
      return (
        <div className={styles.reviewSummaryOuter}>
          <div className={styles.reviewSummaryInner}>
            <div className={styles.productReviewBreakdown}>
              <div className={styles.productReviewTotal}>
                {makeTotalText(reviewsTotalCount, reviewCount, testId)}
                <div className={reviewActions}>
                  {styleThumbnails.length > 0 && (
                    <img src={styleThumbnails[0].src} alt={`${detail.brandName} ${detail.productName}`}/>
                  )}
                </div>
              </div>
              <div className={reviewRatingSummary}>
                <h2>Overall Rating:</h2>
                {numbers.map(num => <p key={num}><span>{overallRating[num] ? overallRating[num] : 0}%</span><Rating rating={num}/></p>)}
              </div>
              {reviewingAShoe && atLeastOneRatingHasPercentage(reviewSummary) ? (
                <div className={styles.reviewFitSummary}>
                  <h2>Customer Fit Survey:</h2>
                  {this.hasPercentage(maxSizeRatingPercentage) ?
                    <p><span>{maxSizeRatingPercentage.percentage ?
                      maxSizeRatingPercentage.percentage : 0}%</span><span>"{maxSizeRatingPercentage.text.substring(0, 23)}"</span></p>
                    : null}
                  {this.hasPercentage(maxWidthRatingPercentage) ?
                    <p><span>{maxWidthRatingPercentage.percentage ?
                      maxWidthRatingPercentage.percentage : 0}%</span><span>"{maxWidthRatingPercentage.text}"</span></p>
                    : null}
                  {this.hasPercentage(maxArchRatingPercentage) ?
                    <p><span>{maxArchRatingPercentage.percentage ?
                      maxArchRatingPercentage.percentage : 0}%</span><span>"{maxArchRatingPercentage.text}"</span></p>
                    : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
    } else {
      return null;
    }
  }

  makeReviewUrl(productId, page, start, orderBy) {
    return `/product/review/${productId}/page/${page}${start ? `/start/${start}` : ''}${orderBy ? `/orderBy/${orderBy}` : ''}`;
  }

  makeTotalText = (reviewsTotalCount, reviewCount, testId) => (
    <div className={reviewsTotalCount} data-test-id={testId('totalReviewCount')}>
      <span>{toThousandsSeparator(reviewCount)}</span> total reviews
    </div>
  );

  render() {
    const {
      product: { reviewData }, product
    } = this.props;
    const { testId } = this.context;
    if (!product.detail || product.isLoading || reviewData.isLoading) {
      return <PageLoader />;
    }

    const { productId, productName } = product.detail;
    const paginationData = this.getPaginationData();

    return (
      <SiteAwareMetadata>
        <div className={styles.wrap} itemScope itemType="http://schema.org/Product">
          <meta itemProp="name" content={productName}/>
          <div className={styles.productReviews}>
            {this.makePageHeading()}
            <a className={styles.writeReviewButton} data-test-id={testId('writeAReviewButton')} href={`/product/review/add/${productId}`}>Write a Review</a>
            {this.makeReviewDetailSummary(paginationData)}
            {this.makeReviewContent(paginationData)}
          </div>
        </div>
      </SiteAwareMetadata>
    );
  }
}

ProductReviews.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func
};

export function mapStateToProps(state) {
  const { reviews: { reviewGallery }, product } = state;
  return {
    product,
    reviewGallery,
    showReviews
  };
}

export default connect(mapStateToProps, {
  loadProductReviewsPage,
  fetchProductReviews,
  fetchReviewSearchResults,
  exitSearchReviews,
  upvoteReview,
  setProductReviewsDocMeta,
  showReviewGalleryModal,
  hideReviewGalleryModal
})(ProductReviews);
